import React, { useEffect, useState } from 'react';
import { Users, Calendar, CreditCard, Crown, BarChart3, CheckCircle, XCircle, Timer, Settings, MessageSquare, HelpCircle, FileText, Upload, UserCheck } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import AdminLayout from '../shared/AdminLayout';
import ManageHairstyles from './ManageHairstyles';
import ManageTestimonials from './ManageTestimonials';
import ManageFAQ from './ManageFAQ';
import ManagePolicies from './ManagePolicies';
import ManageLeads from './ManageLeads';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const AdminDashboard = () => {
  const [reports, setReports] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [brandSettings, setBrandSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    loadData();
  }, []);

  // Tick every 30 seconds for countdown
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  const loadData = async () => {
    try {
      const [reportsRes, customersRes, bookingsRes, paymentsRes, brandRes] = await Promise.all([
        api.getReports(),
        api.getCustomers(),
        api.getAllBookings(),
        api.getAllPayments(),
        api.getAdminBrandSettings()
      ]);

      setReports(reportsRes.data);
      setCustomers(customersRes.data);
      setBookings(bookingsRes.data);
      setPayments(paymentsRes.data);
      setBrandSettings(brandRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const approveCustomer = async (customerId) => {
    try {
      await api.approveCustomer(customerId);
      toast.success('Customer approved successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to approve customer');
    }
  };

  const declineCustomer = async (customerId) => {
    try {
      await api.declineCustomer(customerId);
      toast.success('Customer declined');
      loadData();
    } catch (error) {
      toast.error('Failed to decline customer');
    }
  };

  const handleVerifyPayment = async (paymentId, verified) => {
    try {
      await api.verifyPayment(paymentId, verified);
      toast.success(verified ? 'Payment verified! Confirmation email sent.' : 'Payment marked as unverified. Customer notified.');
      loadData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const saveWhatsAppSettings = async () => {
    try {
      await api.updateBrandSettings({
        whatsapp_link: brandSettings.whatsapp_link,
        whatsapp_enabled: brandSettings.whatsapp_enabled,
      });
      toast.success('WhatsApp settings saved');
    } catch (error) {
      toast.error('Failed to save WhatsApp settings');
    }
  };

  const saveBrandSettings = async () => {
    try {
      await api.updateBrandSettings(brandSettings);
      toast.success('Brand settings saved');
    } catch (error) {
      toast.error('Failed to save brand settings');
    }
  };

  const handleLogoUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await api.uploadFile(file);
      const token = localStorage.getItem('token');
      const url = api.getFileUrl(res.data.path, token);
      setBrandSettings({...brandSettings, [field]: url});
      await api.updateBrandSettings({ [field]: url });
      toast.success('Logo uploaded');
    } catch {
      toast.error('Failed to upload logo');
    }
  };

  // Helper to find booking info for a payment
  const getBookingForPayment = (payment) => {
    return bookings.find(b => b.id === payment.booking_id) || {};
  };

  const getCountdown = (dateStr, timeStr, durationMins) => {
    const appointmentDate = new Date(`${dateStr}T${timeStr}:00`);
    const diff = appointmentDate - now;
    const endTime = new Date(appointmentDate.getTime() + (durationMins || 60) * 60000);

    if (now >= appointmentDate && now <= endTime) {
      return { label: 'Live now', color: 'bg-green-100 text-green-800 border-green-200', pulse: true };
    }
    if (now > endTime) {
      return { label: 'Completed', color: 'bg-[#F3EBE1] text-[#786E66] border-[#E6DCD3]', pulse: false };
    }

    const totalMins = Math.floor(diff / 60000);
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;

    let label;
    if (days > 0) label = `Starts in ${days}d ${hours}h`;
    else if (hours > 0) label = `Starts in ${hours}h ${mins}m`;
    else label = `Starts in ${mins}m`;

    const color = totalMins <= 60
      ? 'bg-amber-100 text-amber-800 border-amber-200'
      : 'bg-blue-50 text-blue-700 border-blue-200';

    return { label, color, pulse: totalMins <= 30 };
  };

  const getPaymentDisplayStatus = (status) => {
    const labels = {
      pending: 'Awaiting Payment',
      pending_review: 'Awaiting Payment',
      verified: 'Payment Received',
      unverified: 'Unverified'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3E2723] border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  const pendingApprovals = customers.filter(c => c.account_status === 'pending_approval').length;
  const pendingPayments = payments.filter(p => p.payment_status === 'pending' || p.payment_status === 'pending_review').length;

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-8">
          <h1 className="font-serif text-4xl text-[#3E2723] mb-2" data-testid="admin-dashboard-title">Admin Dashboard</h1>
          <p className="text-[#786E66]">Manage your business</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-6">
            <Users className="h-8 w-8 text-[#3E2723] mb-4" />
            <p className="text-sm text-[#786E66] mb-1">Total Customers</p>
            <p className="text-3xl font-bold text-[#3E2723]" data-testid="stat-customers">{reports?.total_customers || 0}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-6">
            <Calendar className="h-8 w-8 text-[#3E2723] mb-4" />
            <p className="text-sm text-[#786E66] mb-1">Total Bookings</p>
            <p className="text-3xl font-bold text-[#3E2723]" data-testid="stat-bookings">{reports?.total_bookings || 0}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-6">
            <CreditCard className="h-8 w-8 text-green-600 mb-4" />
            <p className="text-sm text-[#786E66] mb-1">Revenue (NGN)</p>
            <p className="text-3xl font-bold text-[#3E2723]" data-testid="stat-revenue">₦{reports?.total_revenue?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-6">
            <Crown className="h-8 w-8 text-[#C9A961] mb-4" />
            <p className="text-sm text-[#786E66] mb-1">Pending Actions</p>
            <p className="text-3xl font-bold text-[#3E2723]" data-testid="stat-pending">
              {pendingApprovals + pendingPayments}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="customers" className="space-y-6">
          <TabsList className="bg-white rounded-lg p-1 shadow-sm border border-[#E6DCD3] flex flex-wrap">
            <TabsTrigger value="customers" className="gap-2" data-testid="tab-customers"><Users className="h-4 w-4" /> Customers</TabsTrigger>
            <TabsTrigger value="bookings" className="gap-2" data-testid="tab-bookings"><Calendar className="h-4 w-4" /> Bookings</TabsTrigger>
            <TabsTrigger value="payments" className="gap-2" data-testid="tab-payments"><CreditCard className="h-4 w-4" /> Payments</TabsTrigger>
            <TabsTrigger value="hairstyles" className="gap-2" data-testid="tab-hairstyles"><Crown className="h-4 w-4" /> Hairstyles</TabsTrigger>
            <TabsTrigger value="testimonials" className="gap-2" data-testid="tab-testimonials"><MessageSquare className="h-4 w-4" /> Testimonials</TabsTrigger>
            <TabsTrigger value="faq" className="gap-2" data-testid="tab-faq"><HelpCircle className="h-4 w-4" /> FAQ</TabsTrigger>
            <TabsTrigger value="policies" className="gap-2" data-testid="tab-policies"><FileText className="h-4 w-4" /> Policies</TabsTrigger>
            <TabsTrigger value="leads" className="gap-2" data-testid="tab-leads"><UserCheck className="h-4 w-4" /> Leads</TabsTrigger>
            <TabsTrigger value="reports" className="gap-2" data-testid="tab-reports"><BarChart3 className="h-4 w-4" /> Reports</TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings"><Settings className="h-4 w-4" /> Settings</TabsTrigger>
          </TabsList>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Customer Management</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="customers-table">
                  <thead>
                    <tr className="border-b border-[#E6DCD3]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Phone</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer, idx) => (
                      <tr key={customer.id} className="border-b border-[#E6DCD3] last:border-0" data-testid={`customer-row-${idx}`}>
                        <td className="py-3 px-4 text-sm text-[#1A1A1A]">{customer.full_name}</td>
                        <td className="py-3 px-4 text-sm text-[#1A1A1A]">{customer.email}</td>
                        <td className="py-3 px-4 text-sm text-[#1A1A1A]">{customer.phone || '-'}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            customer.account_status === 'approved' ? 'bg-green-100 text-green-800' :
                            customer.account_status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {customer.account_status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {customer.account_status === 'pending_approval' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => approveCustomer(customer.id)}
                                className="bg-green-600 text-white hover:bg-green-700 rounded-lg px-3 py-1 text-sm transition-all"
                                data-testid={`approve-customer-${idx}`}
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => declineCustomer(customer.id)}
                                className="bg-red-600 text-white hover:bg-red-700 rounded-lg px-3 py-1 text-sm transition-all"
                                data-testid={`decline-customer-${idx}`}
                              >
                                Decline
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {customers.length === 0 && (
                      <tr><td colSpan={5} className="py-8 text-center text-[#786E66] text-sm">No customers yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Bookings Tab — Appointment Schedule */}
          <TabsContent value="bookings" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Appointment Schedule</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bookings-table">
                  <thead>
                    <tr className="border-b border-[#E6DCD3]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Hairstyle</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Reference</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Time</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Countdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...bookings]
                      .sort((a, b) => new Date(`${a.appointment_date}T${a.appointment_time}`) - new Date(`${b.appointment_date}T${b.appointment_time}`))
                      .map((booking, idx) => {
                        const countdown = getCountdown(booking.appointment_date, booking.appointment_time, booking.duration_minutes);
                        return (
                          <tr key={booking.id} className="border-b border-[#E6DCD3] last:border-0" data-testid={`booking-row-${idx}`}>
                            <td className="py-4 px-4 text-sm text-[#1A1A1A] font-medium">{booking.customer_name}</td>
                            <td className="py-4 px-4 text-sm text-[#1A1A1A]">{booking.hairstyle_name}</td>
                            <td className="py-4 px-4 text-sm text-[#786E66] font-mono">{booking.booking_reference}</td>
                            <td className="py-4 px-4 text-sm text-[#1A1A1A]">{booking.appointment_date}</td>
                            <td className="py-4 px-4 text-sm text-[#1A1A1A]">{booking.appointment_time}</td>
                            <td className="py-4 px-4">
                              <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${countdown.color}`}
                                data-testid={`booking-countdown-${idx}`}
                              >
                                {countdown.pulse && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-current"></span></span>}
                                <Timer className="h-3 w-3" />
                                {countdown.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    {bookings.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-[#786E66] text-sm">No appointments yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Payment Verification</h2>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="payments-table">
                  <thead>
                    <tr className="border-b border-[#E6DCD3]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Customer</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Reference</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Hairstyle</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Amount</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-[#786E66]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment, idx) => {
                      const relatedBooking = getBookingForPayment(payment);
                      return (
                        <tr key={payment.id} className="border-b border-[#E6DCD3] last:border-0" data-testid={`payment-row-${idx}`}>
                          <td className="py-3 px-4 text-sm text-[#1A1A1A]">{relatedBooking.customer_name || '—'}</td>
                          <td className="py-3 px-4 text-sm text-[#1A1A1A] font-medium">{payment.booking_reference}</td>
                          <td className="py-3 px-4 text-sm text-[#1A1A1A]">{relatedBooking.hairstyle_name || '—'}</td>
                          <td className="py-3 px-4 text-sm text-[#1A1A1A] font-medium">₦{payment.amount?.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-[#786E66]">{new Date(payment.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              (payment.payment_status === 'pending' || payment.payment_status === 'pending_review') ? 'bg-yellow-100 text-yellow-800' :
                              payment.payment_status === 'verified' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`} data-testid={`payment-status-${idx}`}>
                              {getPaymentDisplayStatus(payment.payment_status)}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {(payment.payment_status === 'pending' || payment.payment_status === 'pending_review') && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleVerifyPayment(payment.id, true)}
                                  className="bg-green-600 text-white hover:bg-green-700 rounded-lg px-3 py-1.5 text-sm transition-all inline-flex items-center gap-1"
                                  data-testid={`verify-payment-${idx}`}
                                >
                                  <CheckCircle className="h-3.5 w-3.5" />
                                  Verified
                                </button>
                                <button
                                  onClick={() => handleVerifyPayment(payment.id, false)}
                                  className="bg-red-600 text-white hover:bg-red-700 rounded-lg px-3 py-1.5 text-sm transition-all inline-flex items-center gap-1"
                                  data-testid={`unverify-payment-${idx}`}
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                  Unverified
                                </button>
                              </div>
                            )}
                            {payment.payment_status === 'verified' && (
                              <span className="text-green-600 text-sm font-medium">Payment Received</span>
                            )}
                            {payment.payment_status === 'unverified' && (
                              <span className="text-red-600 text-sm font-medium">Unverified</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {payments.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-[#786E66] text-sm">No payments yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Hairstyles Tab */}
          <TabsContent value="hairstyles" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <ManageHairstyles />
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <ManageTestimonials />
            </div>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <ManageFAQ />
            </div>
          </TabsContent>

          {/* Policies Tab */}
          <TabsContent value="policies" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <ManagePolicies />
            </div>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <ManageLeads />
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
              <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Business Reports</h2>
              <div className="grid md:grid-cols-2 gap-6" data-testid="reports-container">
                <div className="border border-[#E6DCD3] rounded-lg p-6">
                  <h3 className="font-medium text-[#1A1A1A] mb-4">Booking Statistics</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Total Bookings:</span>
                      <span className="font-medium">{reports?.total_bookings || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Confirmed:</span>
                      <span className="font-medium text-green-600">{reports?.confirmed_bookings || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Cancelled:</span>
                      <span className="font-medium text-red-600">{reports?.cancelled_bookings || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="border border-[#E6DCD3] rounded-lg p-6">
                  <h3 className="font-medium text-[#1A1A1A] mb-4">Customer Statistics</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Total Customers:</span>
                      <span className="font-medium">{reports?.total_customers || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Approved:</span>
                      <span className="font-medium text-green-600">{reports?.approved_customers || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Pending Approval:</span>
                      <span className="font-medium text-yellow-600">{reports?.pending_approvals || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#786E66]">Total Revenue:</span>
                      <span className="font-medium text-[#3E2723]">₦{reports?.total_revenue?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            {brandSettings && (
              <>
                {/* Brand Identity */}
                <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
                  <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Brand Identity</h2>
                  <div className="max-w-2xl space-y-6" data-testid="brand-settings-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Business Name</label>
                        <input value={brandSettings.business_name || ''} onChange={e => setBrandSettings({...brandSettings, business_name: e.target.value})} className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="brand-name-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Tagline</label>
                        <input value={brandSettings.tagline || ''} onChange={e => setBrandSettings({...brandSettings, tagline: e.target.value})} className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="brand-tagline-input" />
                      </div>
                    </div>

                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Primary Logo</label>
                      <div className="flex items-center gap-4">
                        {brandSettings.primary_logo ? (
                          <img src={brandSettings.primary_logo} alt="Logo" className="h-16 rounded-lg border border-[#E6DCD3] p-1" />
                        ) : (
                          <div className="h-16 w-32 bg-[#F3EBE1] rounded-lg border border-dashed border-[#E6DCD3] flex items-center justify-center text-xs text-[#786E66]">No logo</div>
                        )}
                        <label className="cursor-pointer bg-[#F3EBE1] hover:bg-[#E6DCD3] text-[#3E2723] rounded-full px-4 py-2 text-sm transition-all inline-flex items-center gap-2">
                          <Upload className="h-4 w-4" /> Upload
                          <input type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e, 'primary_logo')} data-testid="logo-upload" />
                        </label>
                      </div>
                    </div>

                    {/* Favicon Upload */}
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Favicon</label>
                      <div className="flex items-center gap-4">
                        {brandSettings.favicon ? (
                          <img src={brandSettings.favicon} alt="Favicon" className="h-10 w-10 rounded border border-[#E6DCD3]" />
                        ) : (
                          <div className="h-10 w-10 bg-[#F3EBE1] rounded border border-dashed border-[#E6DCD3] flex items-center justify-center text-xs text-[#786E66]">-</div>
                        )}
                        <label className="cursor-pointer bg-[#F3EBE1] hover:bg-[#E6DCD3] text-[#3E2723] rounded-full px-4 py-2 text-sm transition-all inline-flex items-center gap-2">
                          <Upload className="h-4 w-4" /> Upload
                          <input type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e, 'favicon')} data-testid="favicon-upload" />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
                  <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Contact Information</h2>
                  <div className="max-w-2xl space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Contact Email</label>
                        <input type="email" value={brandSettings.contact_email || ''} onChange={e => setBrandSettings({...brandSettings, contact_email: e.target.value})} className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="contact-email-input" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Contact Phone</label>
                        <input value={brandSettings.contact_phone || ''} onChange={e => setBrandSettings({...brandSettings, contact_phone: e.target.value})} placeholder="+234..." className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="contact-phone-input" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Business Address</label>
                      <input value={brandSettings.address || ''} onChange={e => setBrandSettings({...brandSettings, address: e.target.value})} placeholder="Your business address" className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="address-input" />
                    </div>
                  </div>
                </div>

                {/* Social Links */}
                <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
                  <h2 className="font-serif text-2xl text-[#3E2723] mb-6">Social Links</h2>
                  <div className="max-w-2xl space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Instagram URL</label>
                      <input value={brandSettings.instagram || ''} onChange={e => setBrandSettings({...brandSettings, instagram: e.target.value})} placeholder="https://instagram.com/..." className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="instagram-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Facebook URL</label>
                      <input value={brandSettings.facebook || ''} onChange={e => setBrandSettings({...brandSettings, facebook: e.target.value})} placeholder="https://facebook.com/..." className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="facebook-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Twitter/X URL</label>
                      <input value={brandSettings.twitter || ''} onChange={e => setBrandSettings({...brandSettings, twitter: e.target.value})} placeholder="https://x.com/..." className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="twitter-input" />
                    </div>
                  </div>
                </div>

                {/* WhatsApp Settings */}
                <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8">
                  <h2 className="font-serif text-2xl text-[#3E2723] mb-6">WhatsApp Settings</h2>
                  <div className="max-w-2xl space-y-6" data-testid="whatsapp-settings-form">
                    <div>
                      <label className="block text-sm font-medium text-[#1A1A1A] mb-2">WhatsApp Link</label>
                      <input type="url" value={brandSettings.whatsapp_link || ''} onChange={e => setBrandSettings({...brandSettings, whatsapp_link: e.target.value})} placeholder="https://wa.me/message/..." className="w-full px-4 py-2.5 border border-[#E6DCD3] rounded-lg bg-[#FCFBF9] text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]" data-testid="whatsapp-link-input" />
                    </div>
                    <div className="flex items-center justify-between p-4 bg-[#FCFBF9] rounded-lg border border-[#E6DCD3]">
                      <div>
                        <p className="text-sm font-medium text-[#1A1A1A]">Floating WhatsApp Button</p>
                        <p className="text-xs text-[#786E66]">Show WhatsApp button on all public pages</p>
                      </div>
                      <button onClick={() => setBrandSettings({...brandSettings, whatsapp_enabled: !brandSettings.whatsapp_enabled})} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${brandSettings.whatsapp_enabled ? 'bg-[#25D366]' : 'bg-gray-300'}`} data-testid="whatsapp-toggle">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${brandSettings.whatsapp_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Save All */}
                <div className="flex justify-end">
                  <button onClick={saveBrandSettings} className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-10 py-3 text-sm font-medium transition-all hover:-translate-y-0.5" data-testid="save-all-settings">
                    Save All Settings
                  </button>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
